import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Building2,
  Users,
  UserPlus,
  Crown,
  Shield,
  Pencil,
  Eye,
  Trash2,
  Search,
  Loader2,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useDebounce } from '@/hooks/use-debounce'
import {
  adminService,
  type AdminOrganizationDetailResponse,
  type AdminUser,
} from '@/services/admin'
import type { AxiosError } from 'axios'

type OrgMemberRole = 'owner' | 'admin' | 'editor' | 'viewer'

interface OrgMember {
  id: string
  email: string
  full_name: string | null
  role: string
  is_active: boolean
}

interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

function createInitialDialogState<T>(): DialogState<T> {
  return { isOpen: false, isProcessing: false, error: null, context: null }
}

const ROLE_OPTIONS: { value: OrgMemberRole; icon: typeof Crown; color: string }[] = [
  { value: 'owner', icon: Crown, color: 'text-amber-400' },
  { value: 'admin', icon: Shield, color: 'text-purple-400' },
  { value: 'editor', icon: Pencil, color: 'text-blue-400' },
  { value: 'viewer', icon: Eye, color: 'text-gray-400' },
]

function getRoleIcon(role: string) {
  const opt = ROLE_OPTIONS.find((r) => r.value === role)
  if (!opt) return { Icon: Eye, color: 'text-gray-400' }
  return { Icon: opt.icon, color: opt.color }
}

function getRoleBadgeClass(role: string) {
  switch (role) {
    case 'owner':
      return 'bg-amber-500/20 text-amber-400'
    case 'admin':
      return 'bg-purple-500/20 text-purple-400'
    case 'editor':
      return 'bg-blue-500/20 text-blue-400'
    default:
      return 'bg-gray-500/20 text-gray-400'
  }
}

// --- AddMemberDialog ---

interface AddMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  existingMemberIds: string[]
  onAdded: (member: OrgMember) => void
}

function AddMemberDialog({
  open,
  onOpenChange,
  orgId,
  existingMemberIds,
  onAdded,
}: AddMemberDialogProps) {
  const { t } = useTranslation('admin')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [selectedRole, setSelectedRole] = useState<OrgMemberRole>('viewer')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debouncedSearch = useDebounce(searchTerm, 300)

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setUsers([])
      return
    }

    const controller = new AbortController()

    async function searchUsers() {
      setIsSearching(true)
      try {
        const response = await adminService.listUsers(
          { search: debouncedSearch, status: 'active', page: 1, page_size: 10 },
          { signal: controller.signal }
        )
        if (!controller.signal.aborted) {
          setUsers(
            response.users.filter((u) => !existingMemberIds.includes(u.id))
          )
        }
      } catch (err) {
        if ((err as Error).name !== 'CanceledError') {
          setUsers([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false)
        }
      }
    }

    searchUsers()
    return () => controller.abort()
  }, [debouncedSearch, existingMemberIds])

  const handleClose = (isOpen: boolean) => {
    if (!isSubmitting) {
      if (!isOpen) {
        setSearchTerm('')
        setSelectedUser(null)
        setSelectedRole('viewer')
        setUsers([])
        setError(null)
      }
      onOpenChange(isOpen)
    }
  }

  const handleSubmit = async () => {
    if (!selectedUser) return
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await adminService.addOrgMember(orgId, {
        user_id: selectedUser.id,
        role: selectedRole,
      })
      onAdded(response.member)
      handleClose(false)
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>
      setError(axiosError.response?.data?.detail || t('organizations.detail.addError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-gray-900 border-gray-700/50">
        <DialogHeader>
          <DialogTitle className="text-gray-100">
            {t('organizations.detail.addMemberTitle')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('organizations.detail.addMemberDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* User search */}
          <div className="space-y-1.5">
            <Label className="text-gray-300">
              {t('organizations.detail.selectUser')} <span className="text-red-400">*</span>
            </Label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 rounded-md bg-white/5 border border-gray-700/50">
                <div>
                  <p className="text-sm text-gray-100">{selectedUser.full_name || selectedUser.email}</p>
                  <p className="text-xs text-gray-400">{selectedUser.email}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedUser(null)}
                  disabled={isSubmitting}
                >
                  &times;
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input
                    type="text"
                    placeholder={t('organizations.detail.searchUsers')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white/5 border-gray-700/50 focus:border-purple-500/50"
                    disabled={isSubmitting}
                  />
                </div>
                {isSearching && (
                  <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('organizations.detail.searching')}
                  </div>
                )}
                {!isSearching && debouncedSearch.length >= 2 && users.length === 0 && (
                  <p className="text-sm text-gray-500 py-2">
                    {t('organizations.detail.noUsersFound')}
                  </p>
                )}
                {users.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-gray-700/50 p-1">
                    {users.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="w-full text-left px-3 py-2 rounded hover:bg-white/10 transition-colors"
                        onClick={() => {
                          setSelectedUser(user)
                          setSearchTerm('')
                          setUsers([])
                        }}
                      >
                        <p className="text-sm text-gray-100">
                          {user.full_name || user.email}
                        </p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Role select */}
          <div className="space-y-1.5">
            <Label className="text-gray-300">
              {t('organizations.detail.selectRole')} <span className="text-red-400">*</span>
            </Label>
            <Select
              value={selectedRole}
              onValueChange={(v) => setSelectedRole(v as OrgMemberRole)}
              disabled={isSubmitting}
            >
              <SelectTrigger className="bg-white/5 border-gray-700/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass border-gray-700/50">
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="focus:bg-white/5">
                    {t(`organizations.detail.role${opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={isSubmitting}
            className="border-gray-700/50"
          >
            {t('organizations.create.cancel')}
          </Button>
          <Button
            variant="action-purple"
            disabled={isSubmitting || !selectedUser}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('organizations.detail.adding')}
              </>
            ) : (
              t('organizations.detail.addConfirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- EditRoleDialog ---

interface EditRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  member: OrgMember | null
  onUpdated: (member: OrgMember) => void
}

function EditRoleDialog({
  open,
  onOpenChange,
  orgId,
  member,
  onUpdated,
}: EditRoleDialogProps) {
  const { t } = useTranslation('admin')
  const [selectedRole, setSelectedRole] = useState<OrgMemberRole>('viewer')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (member) {
      setSelectedRole(member.role as OrgMemberRole)
    }
  }, [member])

  const handleClose = (isOpen: boolean) => {
    if (!isSubmitting) {
      if (!isOpen) setError(null)
      onOpenChange(isOpen)
    }
  }

  const handleSubmit = async () => {
    if (!member) return
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await adminService.updateOrgMemberRole(orgId, member.id, {
        role: selectedRole,
      })
      onUpdated(response.member)
      handleClose(false)
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>
      setError(axiosError.response?.data?.detail || t('organizations.detail.changeRoleError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm bg-gray-900 border-gray-700/50">
        <DialogHeader>
          <DialogTitle className="text-gray-100">
            {t('organizations.detail.changeRoleTitle')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('organizations.detail.changeRoleDesc', {
              name: member?.full_name || member?.email || '',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-gray-300">{t('organizations.detail.selectRole')}</Label>
            <Select
              value={selectedRole}
              onValueChange={(v) => setSelectedRole(v as OrgMemberRole)}
              disabled={isSubmitting}
            >
              <SelectTrigger className="bg-white/5 border-gray-700/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass border-gray-700/50">
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="focus:bg-white/5">
                    {t(`organizations.detail.role${opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={isSubmitting}
            className="border-gray-700/50"
          >
            {t('organizations.create.cancel')}
          </Button>
          <Button
            variant="action-purple"
            disabled={isSubmitting || selectedRole === member?.role}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('organizations.detail.changingRole')}
              </>
            ) : (
              t('organizations.detail.changeRoleConfirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Main Page ---

export function AdminOrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('admin')

  const [org, setOrg] = useState<AdminOrganizationDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [editRoleDialog, setEditRoleDialog] = useState<DialogState<OrgMember>>(
    createInitialDialogState()
  )
  const [removeDialog, setRemoveDialog] = useState<DialogState<OrgMember>>(
    createInitialDialogState()
  )

  const fetchOrg = useCallback(async (signal?: AbortSignal) => {
    if (!orgId) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await adminService.getOrganization(orgId, signal ? { signal } : undefined)
      if (!signal?.aborted) {
        setOrg(data)
      }
    } catch (err) {
      if ((err as Error).name !== 'CanceledError') {
        setError((err as AxiosError<{ detail: string }>).response?.data?.detail || 'Error loading organization')
        setOrg(null)
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false)
      }
    }
  }, [orgId])

  useEffect(() => {
    const controller = new AbortController()
    fetchOrg(controller.signal)
    return () => controller.abort()
  }, [fetchOrg])

  const handleMemberAdded = (member: OrgMember) => {
    if (!org) return
    setOrg({
      ...org,
      members: [...org.members, member],
      members_count: org.members_count + 1,
    })
  }

  const handleMemberUpdated = (updatedMember: OrgMember) => {
    if (!org) return
    setOrg({
      ...org,
      members: org.members.map((m) =>
        m.id === updatedMember.id ? updatedMember : m
      ),
    })
    setEditRoleDialog(createInitialDialogState())
  }

  const handleConfirmRemove = async () => {
    if (!removeDialog.context || !orgId) return

    setRemoveDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.removeOrgMember(orgId, removeDialog.context.id)
      if (org) {
        setOrg({
          ...org,
          members: org.members.filter((m) => m.id !== removeDialog.context?.id),
          members_count: org.members_count - 1,
        })
      }
      setRemoveDialog(createInitialDialogState())
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>
      setRemoveDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('organizations.detail.removeError'),
      }))
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-700/50 animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 bg-gray-700/50 rounded animate-pulse" />
            <div className="h-4 w-32 bg-gray-700/50 rounded animate-pulse" />
          </div>
        </div>
        <Card variant="glass" className="p-6 animate-pulse">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-700/50 rounded" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  if (error || !org) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          className="gap-2 text-gray-400"
          onClick={() => navigate('/admin/organizations')}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('organizations.detail.back')}
        </Button>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mb-6">
            <Building2 className="w-10 h-10 text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-200 mb-2">
            {error || 'Organization not found'}
          </h3>
        </div>
      </div>
    )
  }

  const existingMemberIds = org.members.map((m) => m.id)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400"
            onClick={() => navigate('/admin/organizations')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{org.name}</h1>
            <p className="text-gray-400 text-sm font-mono">@{org.slug}</p>
          </div>
          {org.is_active ? (
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
              {t('organizations.active')}
            </span>
          ) : (
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
              {t('organizations.inactive')}
            </span>
          )}
        </div>
      </div>

      {/* Info card */}
      {org.description && (
        <Card variant="glass" className="p-4">
          <p className="text-sm text-gray-300">{org.description}</p>
        </Card>
      )}

      {/* Members section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold text-gray-100">
              {t('organizations.detail.membersTitle')}
            </h2>
            <span className="text-sm text-gray-400">
              ({org.members.length})
            </span>
          </div>
          <Button
            variant="action-purple"
            size="sm"
            className="gap-2"
            onClick={() => setAddMemberOpen(true)}
          >
            <UserPlus className="w-4 h-4" />
            {t('organizations.detail.addMember')}
          </Button>
        </div>

        {org.members.length === 0 ? (
          <Card variant="glass" className="p-8">
            <div className="flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">
                {t('organizations.detail.noMembers')}
              </h3>
              <p className="text-gray-500 text-sm text-center">
                {t('organizations.detail.noMembersDesc')}
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {org.members.map((member) => {
              const { Icon, color } = getRoleIcon(member.role)
              return (
                <Card key={member.id} variant="glass" className="p-4 hover:bg-white/10 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-sm font-semibold">
                        {(member.full_name || member.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-100">
                          {member.full_name || member.email}
                        </p>
                        <p className="text-xs text-gray-400">{member.email}</p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getRoleBadgeClass(member.role)}`}
                      >
                        <Icon className={`w-3 h-3 ${color}`} />
                        {t(`organizations.detail.role${member.role.charAt(0).toUpperCase() + member.role.slice(1)}`)}
                      </span>
                      {!member.is_active && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                          {t('organizations.inactive')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1 text-gray-400 hover:text-gray-100"
                        onClick={() =>
                          setEditRoleDialog({
                            isOpen: true,
                            isProcessing: false,
                            error: null,
                            context: member,
                          })
                        }
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        {t('organizations.detail.changeRole')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1 text-red-400 hover:text-red-300"
                        onClick={() =>
                          setRemoveDialog({
                            isOpen: true,
                            isProcessing: false,
                            error: null,
                            context: member,
                          })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('organizations.detail.removeMember')}
                      </Button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddMemberDialog
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        orgId={orgId!}
        existingMemberIds={existingMemberIds}
        onAdded={handleMemberAdded}
      />

      <EditRoleDialog
        open={editRoleDialog.isOpen}
        onOpenChange={(open) =>
          !open && !editRoleDialog.isProcessing && setEditRoleDialog(createInitialDialogState())
        }
        orgId={orgId!}
        member={editRoleDialog.context}
        onUpdated={handleMemberUpdated}
      />

      <ConfirmationDialog
        open={removeDialog.isOpen}
        onOpenChange={(open) =>
          !open && !removeDialog.isProcessing && setRemoveDialog(createInitialDialogState())
        }
        title={t('organizations.detail.removeMemberTitle')}
        description={t('organizations.detail.removeMemberDesc', {
          name:
            removeDialog.context?.full_name || removeDialog.context?.email || '',
        })}
        confirmLabel={t('organizations.detail.removeConfirm')}
        confirmingLabel={t('organizations.detail.removing')}
        confirmVariant="action-red"
        isProcessing={removeDialog.isProcessing}
        error={removeDialog.error}
        onConfirm={handleConfirmRemove}
      />
    </div>
  )
}
